import React, { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ReminderGuide.css';
import {
  RG_Eyebrow,
  RG_Headline_Pre,
  RG_Headline_Em,
  RG_Subhead,
  RG_Tip_Title,
  RG_Tip_Body,
  RG_Tab_Ios,
  RG_Tab_Android,
  RG_Steps_Title,
  RG_Ios_1, RG_Ios_2, RG_Ios_3, RG_Ios_4, RG_Ios_5, RG_Ios_6,
  RG_Android_1, RG_Android_2, RG_Android_3, RG_Android_4, RG_Android_5, RG_Android_6,
  RG_Menu_Note,
  RG_Done,
  RG_Video_Eyebrow,
  RG_Video_Title,
  HTT_Support,
} from '../../Constants';

const PLATFORMS = {
  ios: {
    tab: RG_Tab_Ios,
    steps: [RG_Ios_1, RG_Ios_2, RG_Ios_3, RG_Ios_4, RG_Ios_5, RG_Ios_6],
  },
  android: {
    tab: RG_Tab_Android,
    steps: [RG_Android_1, RG_Android_2, RG_Android_3, RG_Android_4, RG_Android_5, RG_Android_6],
  },
};

// Explainer video. Recorded in German only, so it is hidden for other languages.
const RG_VIDEO_DE = 'https://d1ypa02v8rscne.cloudfront.net/resources/videos/philipp-erklaervideo-2-routine-de%20(1).mp4';

// ?os=ios|android wins, so an email link can pre-select a tab. Otherwise read the device.
const detectPlatform = (override) => {
  if (override && PLATFORMS[override]) return override;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /android/i.test(ua) ? 'android' : 'ios';
};

const ReminderGuide = () => {
  const { t, i18n } = useTranslation();
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoSrc = (i18n.language || 'de').startsWith('de') ? RG_VIDEO_DE : null;
  const playVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setVideoPlaying(true);
    }
  };
  const [searchParams] = useSearchParams();
  const [platform, setPlatform] = useState(() => detectPlatform(searchParams.get('os')));
  const current = PLATFORMS[platform];

  return (
    <div className="rg">
      <section className="rg-hero">
        <span className="rg-eyebrow">{t(RG_Eyebrow)}</span>
        <h1 className="rg-hero__title">
          {t(RG_Headline_Pre)} <em>{t(RG_Headline_Em)}</em>
        </h1>
        <p className="rg-hero__sub">{t(RG_Subhead)}</p>
      </section>

      <section className="rg-body">
        <div className="rg-tip">
          <h2 className="rg-tip__title">{t(RG_Tip_Title)}</h2>
          <p className="rg-tip__body">{t(RG_Tip_Body)}</p>
        </div>

        <div className="rg-steps">
          <h2 className="rg-steps__title">{t(RG_Steps_Title)}</h2>

          <div className="rg-tabs" role="tablist">
            {Object.entries(PLATFORMS).map(([key, p]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={platform === key}
                className={`rg-tab${platform === key ? ' rg-tab--active' : ''}`}
                onClick={() => setPlatform(key)}
              >
                {t(p.tab)}
              </button>
            ))}
          </div>

          <ol className="rg-list">
            {current.steps.map((stepKey, i) => (
              <li key={stepKey} className="rg-list__item">
                <span className="rg-list__num">{i + 1}</span>
                <span className="rg-list__text" dangerouslySetInnerHTML={{ __html: t(stepKey) }} />
              </li>
            ))}
          </ol>

          <p className="rg-note">{t(RG_Menu_Note)}</p>
          <p className="rg-done">{t(RG_Done)}</p>
        </div>

        {videoSrc && (
          <div className="rg-video">
            {/* #t=0.5 makes the browser paint a frame as the cover */}
            <video
              ref={videoRef}
              src={`${videoSrc}#t=0.5`}
              controls={videoPlaying}
              playsInline
              preload="metadata"
              onEnded={() => setVideoPlaying(false)}
            />
            {!videoPlaying && (
              <button type="button" className="rg-video__cover" onClick={playVideo} aria-label={t(RG_Video_Title)}>
                <span className="rg-video__eyebrow">{t(RG_Video_Eyebrow)}</span>
                <span className="rg-video__title">{t(RG_Video_Title)}</span>
                <span className="rg-video__play">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M8 5v14l11-7L8 5z" fill="#103642" />
                  </svg>
                </span>
              </button>
            )}
          </div>
        )}
      </section>

      <footer className="rg-support">{t(HTT_Support)}</footer>
    </div>
  );
};

export default ReminderGuide;
